import React from 'react';
import { Smartphone, Shield, CreditCard, Gift, ShoppingBag, Settings, Bell, Check, Apple, Store } from 'lucide-react';
import useMobile from '../hooks/useMobile';

const ProductsPage = () => {
  const isMobile = useMobile();

  const features = [
    {
      icon: <Shield size={48} color="#7c3aed" />,
      title: "Secure Authentication",
      description: "Account creation and sign-in with Apple and Google for maximum security and convenience"
    },
    {
      icon: <CreditCard size={48} color="#7c3aed" />,
      title: "Multiple Payment Options",
      description: "Accept payments through Stripe, Apple Pay, and Google Pay - whatever your customers prefer"
    },
    {
      icon: <Gift size={48} color="#7c3aed" />,
      title: "Rewards & Loyalty Program",
      description: "Custom rewards program with point tracking to keep customers coming back"
    },
    {
      icon: <ShoppingBag size={48} color="#7c3aed" />,
      title: "Flexible Ordering",
      description: "In-app ordering for both takeout and dine-in experiences"
    },
    {
      icon: <Settings size={48} color="#7c3aed" />,
      title: "Restaurant Management App",
      description: "Control everything: menu, pricing, availability, order notifications, and marketing messages"
    },
    {
      icon: <Bell size={48} color="#7c3aed" />,
      title: "Push Notifications",
      description: "Direct access to your customers with targeted marketing and order updates"
    }
  ];

  return (
    <div className="fade-in" style={{ padding: '60px 0' }}>
      <div className="container">
        <h1 style={{ 
          textAlign: 'center', 
          fontSize: isMobile ? '2.5rem' : '3rem', 
          marginBottom: '20px' 
        }}>
          Everything Your Restaurant Needs
        </h1>
        <p style={{
          textAlign: 'center',
          fontSize: '1.3rem',
          color: '#6b7280',
          marginBottom: '60px',
          maxWidth: '800px',
          margin: '0 auto 60px'
        }}>
          One complete solution for mobile ordering, customer engagement, and restaurant management
        </p>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '40px',
          marginBottom: '100px'
        }}>
          {features.map((feature, idx) => (
            <div key={idx} className="card" style={{
              backgroundColor: '#fff',
              padding: '40px',
              borderRadius: '12px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '20px'
            }}>
              <div style={{ flexShrink: 0 }}>{feature.icon}</div>
              <div>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '10px' }}>{feature.title}</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Section */}
        <div style={{
          background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
          borderRadius: '24px',
          padding: isMobile ? '40px 20px' : '80px',
          textAlign: 'center',
          color: 'white',
          marginBottom: '80px'
        }}>
          <h2 style={{ 
            fontSize: isMobile ? '2rem' : '2.5rem', 
            marginBottom: '20px' 
          }}>
            Super Simple Pricing
          </h2>
          <p style={{ 
            fontSize: '1.2rem', 
            marginBottom: '40px',
            opacity: 0.9 
          }}>
            No setup fees. No monthly fees. No hidden costs.
          </p>
          
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '600px',
            margin: '0 auto',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ 
              fontSize: isMobile ? '3rem' : '4rem', 
              fontWeight: 'bold',
              marginBottom: '20px' 
            }}>
              Payment Processing + 5%
            </div>
            <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              You only pay when you make money. Standard payment processing rates apply, plus our 5% platform fee.
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            maxWidth: '400px',
            margin: '40px auto 0',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Check size={24} style={{ marginRight: '10px', flexShrink: 0 }} />
              <span>No monthly subscription fees</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Check size={24} style={{ marginRight: '10px', flexShrink: 0 }} />
              <span>No setup or installation costs</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Check size={24} style={{ marginRight: '10px', flexShrink: 0 }} />
              <span>Free updates and new features</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Check size={24} style={{ marginRight: '10px', flexShrink: 0 }} />
              <span>24/7 customer support included</span>
            </div>
          </div>
        </div>

        {/* Two Apps Section */}
        <div style={{ marginBottom: '80px' }}>
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: isMobile ? '2rem' : '2.5rem', 
            marginBottom: '60px' 
          }}>
            Two Powerful Apps, One Complete Solution
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '40px'
          }}>
            {/* Customer App */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
                color: 'white',
                padding: '40px',
                textAlign: 'center'
              }}>
                <Smartphone size={64} />
                <h3 style={{ fontSize: '2rem', marginTop: '20px' }}>Customer App</h3>
                <p style={{ opacity: 0.9 }}>Available on iOS & Android</p>
              </div>
              <div style={{ padding: '40px' }}>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {[
                    "Your restaurant's branding",
                    "Easy menu browsing",
                    "Quick reordering",
                    "Order tracking",
                    "Rewards & points",
                    "Multiple payment options",
                    "Order history",
                    "Push notifications"
                  ].map((item, idx) => (
                    <li key={idx} style={{ 
                      marginBottom: '15px', 
                      display: 'flex', 
                      alignItems: 'center' 
                    }}>
                      <Check size={20} color="#10b981" style={{ marginRight: '10px' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ 
                  marginTop: '30px', 
                  display: 'flex', 
                  gap: '10px',
                  justifyContent: 'center' 
                }}>
                  <Apple size={24} color="#000" />
                  <Store size={24} color="#000" />
                </div>
              </div>
            </div>

            {/* Management App */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #059669, #047857)',
                color: 'white',
                padding: '40px',
                textAlign: 'center'
              }}>
                <Settings size={64} />
                <h3 style={{ fontSize: '2rem', marginTop: '20px' }}>Management App</h3>
                <p style={{ opacity: 0.9 }}>For Restaurant Owners</p>
              </div>
              <div style={{ padding: '40px' }}>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {[
                    "Real-time order management",
                    "Menu & pricing control",
                    "Item availability toggle",
                    "Order notifications",
                    "Customer analytics",
                    "Marketing campaigns",
                    "Revenue reports",
                    "Sales performance tracking"
                  ].map((item, idx) => (
                    <li key={idx} style={{ 
                      marginBottom: '15px', 
                      display: 'flex', 
                      alignItems: 'center' 
                    }}>
                      <Check size={20} color="#10b981" style={{ marginRight: '10px' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p style={{ 
                  marginTop: '30px', 
                  textAlign: 'center',
                  color: '#6b7280' 
                }}>
                  Available on iOS, Android & Web
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div style={{
          backgroundColor: '#f9fafb',
          borderRadius: '16px',
          padding: isMobile ? '40px 20px' : '60px',
          textAlign: 'center'
        }}>
          <h2 style={{ 
            fontSize: isMobile ? '2rem' : '2.5rem', 
            marginBottom: '20px' 
          }}>
            Ready to Grow Your Restaurant?
          </h2>
          <p style={{ 
            fontSize: '1.2rem', 
            color: '#6b7280', 
            marginBottom: '40px',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }}>
            Join restaurants across the country who are increasing sales and delighting customers with Krafty apps
          </p>
          <button style={{
            backgroundColor: '#7c3aed',
            color: 'white',
            border: 'none',
            padding: '16px 40px',
            borderRadius: '8px',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
            Get Started Today
          </button>
          <p style={{ 
            marginTop: '20px', 
            color: '#6b7280' 
          }}>
            No credit card required to get started
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;